import { useState, useEffect, useCallback } from 'react';
import QRCode from 'qrcode';
import { hotelApi } from '../../hotelApi';

const STATUS_COLORS = {
  available: { bg: '#e6f4ea', color: '#137333', border: '#34a85340' },
  occupied:  { bg: '#fce8e6', color: '#c5221f', border: '#c5221f40' },
  reserved:  { bg: '#fef3c7', color: '#b45309', border: '#b4530940' },
};

function OrderModal({ order, onClose, onAddItems, onGoBill, menuItems, categories }) {
  const [newItems, setNewItems] = useState([]);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('');

  const filtered = menuItems.filter(m =>
    (catFilter === '' || String(m.category_id) === catFilter) &&
    (search === '' || m.name.toLowerCase().includes(search.toLowerCase()))
  );

  const addItem = (m) => {
    const ex = newItems.find(i => i.menuItemId === m.id);
    if (ex) setNewItems(items => items.map(i => i.menuItemId === m.id ? { ...i, quantity: i.quantity + 1 } : i));
    else setNewItems(items => [...items, { menuItemId: m.id, name: m.name, price: m.price, quantity: 1 }]);
  };
  const removeItem = (id) => setNewItems(items => items.filter(i => i.menuItemId !== id));

  const subtotal = order.items ? order.items.reduce((s, i) => s + i.price * i.quantity, 0) : 0;
  const newSubtotal = newItems.reduce((s, i) => s + i.price * i.quantity, 0);

  const handleAddMore = async () => {
    if (newItems.length === 0) return;
    await hotelApi.addOrderItems(order.id, { items: newItems });
    setNewItems([]);
    onAddItems();
  };

  const fmtRs = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
  const statusBadge = (s) => {
    const map = { pending: ['#fef3c7','#b45309'], preparing: ['#fff3e0','#e65100'], served: ['#e6f4ea','#137333'] };
    const [bg, col] = map[s] || ['#f1f3f4','#5f6368'];
    return <span style={{ background: bg, color: col, borderRadius: 10, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>{s}</span>;
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 2000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 800,
        maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0,0,0,.4)' }}>
        <div style={{ padding: '16px 20px', background: '#0d3321', color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>
              Table {order.table_no} — Order #{order.order_no}
            </div>
            <div style={{ fontSize: 12, color: '#a8d5b8' }}>
              {order.items?.length || 0} items · Subtotal: {fmtRs(subtotal)}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onGoBill}
              style={{ background: '#34a853', color: '#fff', border: 'none', borderRadius: 8,
                padding: '6px 14px', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
              🧾 Bill
            </button>
            <button onClick={onClose}
              style={{ background: 'rgba(255,255,255,.2)', color: '#fff', border: 'none',
                borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontWeight: 700, fontSize: 16 }}>
              ×
            </button>
          </div>
        </div>

        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {/* Current items */}
          <div style={{ flex: 1, overflow: 'auto', padding: 16, borderRight: '1px solid #e8eaed' }}>
            <div style={{ fontWeight: 700, marginBottom: 12, color: '#0d3321' }}>Current Items</div>
            {order.items?.length === 0 && <div style={{ color: '#888', fontSize: 13 }}>No items yet</div>}
            {(order.items || []).map(item => (
              <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between',
                alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f1f3f4' }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>{item.item_name}</div>
                  <div style={{ fontSize: 12, color: '#888' }}>Qty: {item.quantity} × {fmtRs(item.price)}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {statusBadge(item.status)}
                  <span style={{ fontWeight: 700 }}>{fmtRs(item.price * item.quantity)}</span>
                </div>
              </div>
            ))}
            {order.items?.length > 0 && (
              <div style={{ marginTop: 12, paddingTop: 8, borderTop: '2px solid #e8eaed',
                display: 'flex', justifyContent: 'space-between', fontWeight: 700 }}>
                <span>Subtotal</span><span>{fmtRs(subtotal)}</span>
              </div>
            )}
          </div>

          {/* Add more items */}
          <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
            <div style={{ fontWeight: 700, marginBottom: 8, color: '#0d3321' }}>Add Items</div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search items…" className="form-control" style={{ flex: 1, fontSize: 13 }} />
              <select value={catFilter} onChange={e => setCatFilter(e.target.value)}
                className="form-control" style={{ fontSize: 13, minWidth: 100 }}>
                <option value="">All</option>
                {categories.map(c => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
              </select>
            </div>
            <div style={{ maxHeight: 200, overflow: 'auto', marginBottom: 12 }}>
              {filtered.map(m => (
                <div key={m.id} onClick={() => addItem(m)}
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '7px 10px', borderRadius: 8, cursor: 'pointer', marginBottom: 2,
                    background: '#f8faf8', border: '1px solid #e6f4ea' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#e6f4ea'}
                  onMouseLeave={e => e.currentTarget.style.background = '#f8faf8'}
                >
                  <span style={{ fontSize: 13 }}>
                    {m.is_veg ? '🟢' : '🔴'} {m.name}
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#137333' }}>{fmtRs(m.price)}</span>
                </div>
              ))}
            </div>
            {newItems.length > 0 && (
              <div>
                <div style={{ fontWeight: 600, marginBottom: 6, fontSize: 13 }}>Selected:</div>
                {newItems.map(i => (
                  <div key={i.menuItemId} style={{ display: 'flex', justifyContent: 'space-between',
                    alignItems: 'center', fontSize: 13, marginBottom: 4 }}>
                    <span>{i.name} × {i.quantity}</span>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <span style={{ color: '#137333', fontWeight: 700 }}>{fmtRs(i.price * i.quantity)}</span>
                      <button onClick={() => removeItem(i.menuItemId)}
                        style={{ background: 'none', border: 'none', color: '#c5221f', cursor: 'pointer', fontWeight: 700 }}>×</button>
                    </div>
                  </div>
                ))}
                <div style={{ marginTop: 8, fontWeight: 700, display: 'flex', justifyContent: 'space-between' }}>
                  <span>New subtotal</span><span style={{ color: '#137333' }}>{fmtRs(newSubtotal)}</span>
                </div>
                <button onClick={handleAddMore}
                  style={{ marginTop: 10, width: '100%', background: '#137333', color: '#fff',
                    border: 'none', borderRadius: 8, padding: '9px', cursor: 'pointer',
                    fontWeight: 600, fontSize: 14 }}>
                  ➕ Add to Order
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function QuickOrderModal({ table, onClose, menuItems, categories, onCreated }) {
  const [selectedItems, setSelectedItems] = useState([]);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [saving, setSaving] = useState(false);

  const filtered = menuItems.filter(m =>
    m.is_available &&
    (catFilter === '' || String(m.category_id) === catFilter) &&
    (search === '' || m.name.toLowerCase().includes(search.toLowerCase()))
  );

  const addItem = (m) => {
    const ex = selectedItems.find(i => i.menuItemId === m.id);
    if (ex) setSelectedItems(items => items.map(i => i.menuItemId === m.id ? { ...i, quantity: i.quantity + 1 } : i));
    else setSelectedItems(items => [...items, { menuItemId: m.id, name: m.name, price: m.price, quantity: 1 }]);
  };
  const removeItem = (id) => setSelectedItems(items => items.filter(i => i.menuItemId !== id));

  const fmtRs = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
  const subtotal = selectedItems.reduce((s, i) => s + i.price * i.quantity, 0);

  const handleSubmit = async () => {
    if (selectedItems.length === 0) return;
    setSaving(true);
    try {
      await hotelApi.createOrder({ tableId: table.id, items: selectedItems });
      onCreated();
    } finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 2000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 600,
        maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0,0,0,.4)' }}>
        <div style={{ padding: '16px 20px', background: '#0d3321', color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontWeight: 700, fontSize: 16 }}>
            New Order — Table {table.table_no}
            {table.section && <span style={{ fontSize: 12, fontWeight: 400, color: '#a8d5b8', marginLeft: 8 }}>{table.section}</span>}
          </div>
          <button onClick={onClose}
            style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 20, fontWeight: 700 }}>×</button>
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search items…" className="form-control" style={{ flex: 1 }} />
            <select value={catFilter} onChange={e => setCatFilter(e.target.value)}
              className="form-control" style={{ minWidth: 120 }}>
              <option value="">All Categories</option>
              {categories.map(c => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
            </select>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 8, marginBottom: 16 }}>
            {filtered.map(m => (
              <div key={m.id} onClick={() => addItem(m)}
                style={{ background: '#f8faf8', border: '1px solid #e6f4ea', borderRadius: 10,
                  padding: '10px 12px', cursor: 'pointer', textAlign: 'center' }}
                onMouseEnter={e => e.currentTarget.style.background = '#e6f4ea'}
                onMouseLeave={e => e.currentTarget.style.background = '#f8faf8'}
              >
                <div style={{ fontSize: 13, fontWeight: 600 }}>{m.is_veg ? '🟢' : '🔴'} {m.name}</div>
                <div style={{ fontSize: 13, color: '#137333', fontWeight: 700, marginTop: 4 }}>{fmtRs(m.price)}</div>
              </div>
            ))}
          </div>

          {selectedItems.length > 0 && (
            <div style={{ borderTop: '2px solid #e6f4ea', paddingTop: 12 }}>
              <div style={{ fontWeight: 700, marginBottom: 8, color: '#0d3321' }}>Order Summary</div>
              {selectedItems.map(i => (
                <div key={i.menuItemId} style={{ display: 'flex', justifyContent: 'space-between',
                  alignItems: 'center', marginBottom: 6, fontSize: 14 }}>
                  <span>{i.name} × {i.quantity}</span>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span style={{ fontWeight: 600 }}>{fmtRs(i.price * i.quantity)}</span>
                    <button onClick={() => removeItem(i.menuItemId)}
                      style={{ background: '#fce8e6', color: '#c5221f', border: 'none', borderRadius: 6,
                        width: 22, height: 22, cursor: 'pointer', fontWeight: 700, lineHeight: 1 }}>×</button>
                  </div>
                </div>
              ))}
              <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700,
                borderTop: '1px solid #e8eaed', paddingTop: 8, marginTop: 8 }}>
                <span>Total</span><span style={{ color: '#137333' }}>{fmtRs(subtotal)}</span>
              </div>
            </div>
          )}
        </div>

        <div style={{ padding: '12px 16px', borderTop: '1px solid #e8eaed', display: 'flex', gap: 8 }}>
          <button onClick={handleSubmit} disabled={saving || selectedItems.length === 0}
            style={{ flex: 1, background: selectedItems.length > 0 ? '#137333' : '#ccc',
              color: '#fff', border: 'none', borderRadius: 8, padding: '10px',
              cursor: selectedItems.length > 0 ? 'pointer' : 'not-allowed', fontWeight: 700, fontSize: 15 }}>
            {saving ? 'Placing…' : `Place Order (${fmtRs(subtotal)})`}
          </button>
          <button onClick={onClose}
            style={{ background: '#f1f3f4', border: 'none', borderRadius: 8, padding: '10px 20px',
              cursor: 'pointer', fontWeight: 600 }}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

const EMPTY_TABLE = { tableNo: '', section: '', capacity: 4 };

function QRModal({ table, ownersid, onClose }) {
  const [imgSrc, setImgSrc] = useState('');
  const [copied, setCopied] = useState(false);
  const base = process.env.PUBLIC_URL || '';
  const menuUrl = `${window.location.origin}${base}/public/${ownersid}/menu?table=${encodeURIComponent(table.table_no)}`;

  useEffect(() => {
    QRCode.toDataURL(menuUrl, { width: 240, margin: 2, color: { dark: '#0d3321', light: '#ffffff' } })
      .then(setImgSrc);
  }, [menuUrl]);

  const handlePrint = () => {
    const w = window.open('', '_blank');
    w.document.write(`<html><body style="text-align:center;font-family:sans-serif;padding:24px">
      <h2>Table ${table.table_no}${table.section ? ' — ' + table.section : ''}</h2>
      <img src="${imgSrc}" style="width:200px;height:200px"/><br/>
      <p style="font-size:12px;color:#666;word-break:break-all">${menuUrl}</p>
      <p style="color:#888;font-size:13px">Scan to view menu &amp; order</p>
    </body></html>`);
    w.document.close(); w.print();
  };

  const handleWhatsApp = () => {
    const msg = `🍽️ Table ${table.table_no} — Tap the link below to view the menu & place your order:\n${menuUrl}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(menuUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 3000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: '28px 32px', maxWidth: 360, width: '100%',
        boxShadow: '0 20px 60px rgba(0,0,0,.4)', textAlign: 'center' }}>
        <div style={{ fontWeight: 800, fontSize: 18, color: '#0d3321', marginBottom: 4 }}>
          Table {table.table_no}
        </div>
        {table.section && <div style={{ color: '#888', fontSize: 13, marginBottom: 12 }}>{table.section}</div>}
        {imgSrc
          ? <img src={imgSrc} alt="QR Code" style={{ width: 200, height: 200, borderRadius: 8,
              border: '1px solid #e8eaed', display: 'block', margin: '0 auto 12px' }} />
          : <div style={{ width: 200, height: 200, display: 'flex', alignItems: 'center',
              justifyContent: 'center', margin: '0 auto 12px', background: '#f8faf8', borderRadius: 8 }}>Generating…</div>
        }
        <div style={{ fontSize: 11, color: '#888', wordBreak: 'break-all', marginBottom: 16,
          background: '#f8faf8', borderRadius: 6, padding: '6px 10px' }}>{menuUrl}</div>

        {/* Share row */}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 10 }}>
          <button onClick={handleWhatsApp}
            style={{ background: '#25d366', color: '#fff', border: 'none', borderRadius: 8,
              padding: '9px 16px', cursor: 'pointer', fontWeight: 700, fontSize: 13, flex: 1 }}>
            📲 WhatsApp
          </button>
          <button onClick={handleCopy}
            style={{ background: copied ? '#137333' : '#e8f5e9', color: copied ? '#fff' : '#137333',
              border: 'none', borderRadius: 8, padding: '9px 16px', cursor: 'pointer',
              fontWeight: 700, fontSize: 13, flex: 1, transition: 'background .2s,color .2s' }}>
            {copied ? '✓ Copied!' : '🔗 Copy Link'}
          </button>
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
          <button onClick={handlePrint}
            style={{ background: '#137333', color: '#fff', border: 'none', borderRadius: 8,
              padding: '8px 18px', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
            🖨️ Print
          </button>
          <button onClick={onClose}
            style={{ background: '#f1f3f4', border: 'none', borderRadius: 8,
              padding: '8px 18px', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function ManageTables({ tables, onRefresh, ownersid }) {
  const [form, setForm]       = useState(EMPTY_TABLE);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');
  const [qrTable, setQrTable] = useState(null);

  const startEdit = (t) => { setEditing(t.id); setForm({ tableNo: t.table_no, section: t.section || '', capacity: t.capacity }); setError(''); };
  const cancelEdit = () => { setEditing(null); setForm(EMPTY_TABLE); setError(''); };

  const handleSave = async () => {
    if (!form.tableNo.toString().trim()) { setError('Table number is required'); return; }
    setSaving(true); setError('');
    try {
      if (editing) {
        await hotelApi.updateTable(editing, form);
      } else {
        await hotelApi.createTable(form);
        setForm(EMPTY_TABLE);
      }
      setEditing(null);
      onRefresh();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this table?')) return;
    try { await hotelApi.deleteTable(id); onRefresh(); }
    catch (e) { alert('Delete failed: ' + e.message); }
  };

  const inp = { className: 'form-control', style: { fontSize: 13, padding: '5px 10px' } };

  return (
    <div>
      {/* Add / Edit form */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header">
          <div className="card-title">{editing ? 'Edit Table' : 'Add New Table'}</div>
        </div>
        <div className="card-body">
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 3 }}>Table No *</label>
              <input {...inp} style={{ ...inp.style, width: 100 }} placeholder="e.g. T1"
                value={form.tableNo} onChange={e => setForm(f => ({ ...f, tableNo: e.target.value }))} />
            </div>
            <div>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 3 }}>Section</label>
              <input {...inp} style={{ ...inp.style, width: 140 }} placeholder="e.g. Indoor"
                value={form.section} onChange={e => setForm(f => ({ ...f, section: e.target.value }))} />
            </div>
            <div>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 3 }}>Capacity</label>
              <input {...inp} type="number" min={1} max={50} style={{ ...inp.style, width: 80 }}
                value={form.capacity} onChange={e => setForm(f => ({ ...f, capacity: parseInt(e.target.value) || 2 }))} />
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button className="btn btn-primary" style={{ fontSize: 13, padding: '6px 18px' }}
                onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : editing ? '💾 Update' : '➕ Add Table'}
              </button>
              {editing && (
                <button className="btn btn-secondary" style={{ fontSize: 13, padding: '6px 14px' }}
                  onClick={cancelEdit}>Cancel</button>
              )}
            </div>
          </div>
          {error && <div style={{ color: '#c5221f', fontSize: 13, marginTop: 8 }}>{error}</div>}
        </div>
      </div>

      {/* Tables list */}
      <div className="card">
        <div className="card-header">
          <div className="card-title">All Tables ({tables.length})</div>
        </div>
        <div className="card-body" style={{ padding: 0, overflowX: 'auto' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Table No</th>
                <th>Section</th>
                <th>Capacity</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {tables.length === 0 && (
                <tr><td colSpan={5} style={{ textAlign: 'center', color: '#888', padding: 24 }}>
                  No tables yet. Add your first table above.
                </td></tr>
              )}
              {tables.map(t => (
                <tr key={t.id}>
                  <td><strong>{t.table_no}</strong></td>
                  <td>{t.section || <span style={{ color: '#ccc' }}>—</span>}</td>
                  <td>{t.capacity}</td>
                  <td>
                    <span style={{
                      background: STATUS_COLORS[t.status]?.bg || STATUS_COLORS.available.bg,
                      color: STATUS_COLORS[t.status]?.color || STATUS_COLORS.available.color,
                      borderRadius: 10, padding: '2px 8px', fontSize: 11, fontWeight: 600
                    }}>{t.status || 'available'}</span>
                  </td>
                  <td>
                    {ownersid && (
                      <button className="btn-sm" style={{ marginRight: 4, background: '#e8f0fe', color: '#1a73e8', border: '1px solid #c5d8fd' }}
                        onClick={() => setQrTable(t)} title="Show QR code">QR</button>
                    )}
                    <button className="btn-sm btn-secondary" style={{ marginRight: 4 }}
                      onClick={() => startEdit(t)}>Edit</button>
                    <button className="btn-sm" style={{ background: '#fce8e6', color: '#c5221f', border: '1px solid #f5c6c6' }}
                      onClick={() => handleDelete(t.id)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {qrTable && ownersid && (
          <QRModal table={qrTable} ownersid={ownersid} onClose={() => setQrTable(null)} />
        )}
      </div>
    </div>
  );
}

export default function HotelTables({ role, ownersid, onGoBilling }) {
  const [tab, setTab]           = useState('floor');
  const [tables, setTables]     = useState([]);
  const [orders, setOrders]     = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [quickTable, setQuickTable] = useState(null);
  const [viewOrder, setViewOrder]   = useState(null);
  const [viewOrderData, setViewOrderData] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      hotelApi.getTables(),
      hotelApi.getOrders(),
      hotelApi.getMenuItems(),
      hotelApi.getCategories(),
    ]).then(([t, o, m, c]) => {
      setTables(t || []);
      setOrders(o || []);
      setMenuItems(m || []);
      setCategories((c || []).slice().sort((a, b) => a.name.localeCompare(b.name)));
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const openOrderModal = async (orderId) => {
    const data = await hotelApi.getOrder(orderId);
    setViewOrderData(data);
    setViewOrder(orderId);
  };

  const fmtRs = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
  const elapsed = (t) => {
    const ms = Date.now() - new Date(t).getTime();
    const m = Math.floor(ms / 60000);
    if (m < 60) return `${m}m`;
    return `${Math.floor(m / 60)}h ${m % 60}m`;
  };

  const statusBadge = (s) => {
    const sc = STATUS_COLORS[s] || STATUS_COLORS.available;
    return <span style={{ background: sc.bg, color: sc.color, borderRadius: 10,
      padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>{s}</span>;
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>Loading tables…</div>;

  return (
    <div>
      <div className="tabs" style={{ marginBottom: 16 }}>
        <button className={`tab${tab === 'floor' ? ' active' : ''}`} onClick={() => setTab('floor')}>
          🗺️ Floor Plan
        </button>
        <button className={`tab${tab === 'orders' ? ' active' : ''}`} onClick={() => setTab('orders')}>
          📋 All Orders ({orders.length})
        </button>
        {(role === 'admin' || role === 'manager') && (
          <button className={`tab${tab === 'manage' ? ' active' : ''}`} onClick={() => setTab('manage')}>
            ⚙️ Manage Tables
          </button>
        )}
      </div>

      {tab === 'floor' && (
        <div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
            {tables.length === 0 && (
              <div style={{ padding: 32, color: '#888', fontSize: 14 }}>
                No tables configured.{' '}
                {(role === 'admin' || role === 'manager')
                  ? <span>Go to the <button style={{ background: 'none', border: 'none', color: '#137333', cursor: 'pointer', fontWeight: 700, padding: 0, fontSize: 14 }} onClick={() => setTab('manage')}>⚙️ Manage Tables</button> tab to add tables.</span>
                  : 'Ask your admin to add tables.'}
              </div>
            )}
            {tables.map(t => {
              const sc = STATUS_COLORS[t.status] || STATUS_COLORS.available;
              return (
                <div key={t.id}
                  onClick={() => {
                    if (t.status === 'occupied' && t.open_order_id) {
                      openOrderModal(t.open_order_id);
                    } else if (t.status === 'available') {
                      setQuickTable(t);
                    }
                  }}
                  style={{
                    background: sc.bg, border: `2px solid ${sc.color}50`, borderRadius: 16,
                    padding: '16px 20px', cursor: 'pointer', minWidth: 140, textAlign: 'center',
                    transition: 'transform .15s, box-shadow .15s',
                    boxShadow: '0 2px 8px rgba(0,0,0,.08)',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.03)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,.15)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,.08)'; }}
                >
                  <div style={{ fontSize: 28, marginBottom: 4 }}>🍽️</div>
                  <div style={{ fontWeight: 800, fontSize: 18, color: sc.color }}>{t.table_no}</div>
                  {t.section && <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{t.section}</div>}
                  <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>Cap: {t.capacity}</div>
                  <div style={{ marginTop: 8 }}>{statusBadge(t.status)}</div>
                  {t.status === 'occupied' && t.open_order_no && (
                    <div style={{ fontSize: 11, color: sc.color, marginTop: 6, fontWeight: 600 }}>
                      #{t.open_order_no}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div style={{ marginTop: 12, fontSize: 12, color: '#888', display: 'flex', gap: 16 }}>
            <span style={{ color: '#137333' }}>🟢 Available — click to create order</span>
            <span style={{ color: '#c5221f' }}>🔴 Occupied — click to view/add items</span>
          </div>
        </div>
      )}

      {tab === 'orders' && (
        <div className="card">
          <div className="card-header">
            <div className="card-title">Open Orders</div>
            <button className="btn btn-primary" style={{ fontSize: 13, padding: '5px 14px' }}
              onClick={load}>↻ Refresh</button>
          </div>
          <div className="card-body" style={{ padding: 0, overflowX: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Table</th>
                  <th>Order No</th>
                  <th>Items</th>
                  <th>Subtotal</th>
                  <th>Time</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders.length === 0 && (
                  <tr><td colSpan={7} style={{ textAlign: 'center', color: '#888', padding: 24 }}>
                    No open orders
                  </td></tr>
                )}
                {orders.map(o => (
                  <tr key={o.id}>
                    <td><strong>{o.table_no || '—'}</strong><br /><small style={{ color: '#888' }}>{o.section}</small></td>
                    <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{o.order_no}</td>
                    <td>{o.item_count}</td>
                    <td style={{ fontWeight: 600, color: '#137333' }}>{fmtRs(o.subtotal)}</td>
                    <td style={{ fontSize: 12, color: '#888' }}>{elapsed(o.created_at)}</td>
                    <td>{statusBadge(o.status)}</td>
                    <td>
                      <button className="btn-sm btn-secondary" style={{ marginRight: 4 }}
                        onClick={() => openOrderModal(o.id)}>View</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'manage' && (
        <ManageTables tables={tables} onRefresh={load} ownersid={ownersid} />
      )}

      {quickTable && (
        <QuickOrderModal
          table={quickTable}
          menuItems={menuItems}
          categories={categories}
          onClose={() => setQuickTable(null)}
          onCreated={() => { setQuickTable(null); load(); }}
        />
      )}

      {viewOrder && viewOrderData && (
        <OrderModal
          order={viewOrderData}
          menuItems={menuItems}
          categories={categories}
          onClose={() => { setViewOrder(null); setViewOrderData(null); }}
          onAddItems={() => openOrderModal(viewOrder)}
          onGoBill={() => {
            const id = viewOrder;
            setViewOrder(null);
            setViewOrderData(null);
            if (onGoBilling) onGoBilling(id);
          }}
        />
      )}
    </div>
  );
}
